import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ChevronDown, GraduationCap, LogOut, Menu, ShieldCheck, User } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const NAV_LINKS = [
  { to: "/career-programs", label: "Career Programs" },
  { to: "/wilp-bvoc", label: "WILP B.Voc" },
  { to: "/career-pathways", label: "Pathways" },
  { to: "/internship", label: "Internship" },
  { to: "/admissions", label: "Admissions" },
  { to: "/events", label: "Workshops" },
  { to: "/blog", label: "Blog" },
  { to: "/verify", label: "Verify" },
];

export default function Navbar() {
  const [categories, setCategories] = useState([]);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const linkCls = ({ isActive }) =>
    `text-sm font-medium transition-colors hover:text-orange-400 ${isActive ? "text-orange-400" : "text-slate-300"}`;

  return (
    <header className="fixed top-0 inset-x-0 z-50">
      <div className="bg-orange-600 text-white text-center text-xs sm:text-sm py-1.5 px-4 font-medium" data-testid="announcement-bar">
        Admissions Open for 2026 FutureSkills Batches in Jorhat
        <Link to="/admissions" className="underline underline-offset-2 ml-2 font-semibold" data-testid="announcement-apply-link">Apply Now</Link>
      </div>
      <div className="glass border-x-0 border-t-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="nav-logo">
            <div className="w-9 h-9 rounded-lg bg-orange-600 flex items-center justify-center">
              <GraduationCap className="w-5 h-5 text-white" />
            </div>
            <div className="leading-tight">
              <div className="font-display font-extrabold text-white text-sm sm:text-base tracking-tight">XTM TECHNOLOGIES</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-orange-400 font-semibold">FutureSkills Academy</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-6" data-testid="main-nav">
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 text-sm font-medium text-slate-300 hover:text-orange-400 transition-colors outline-none" data-testid="nav-courses-dropdown">
                Courses <ChevronDown className="w-3.5 h-3.5" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-navy-surface border-white/10 text-slate-200 w-80">
                <DropdownMenuItem onClick={() => navigate("/courses")} className="cursor-pointer focus:bg-navy-hover" data-testid="nav-all-courses">
                  All Courses
                </DropdownMenuItem>
                {categories.map((c) => (
                  <DropdownMenuItem key={c.slug} onClick={() => navigate(`/courses/category/${c.slug}`)} className="cursor-pointer focus:bg-navy-hover" data-testid={`nav-cat-${c.slug}`}>
                    {c.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            {NAV_LINKS.map((l) => (
              <NavLink key={l.to} to={l.to} className={linkCls} data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center gap-2 text-sm text-slate-200 hover:text-orange-400 transition-colors outline-none" data-testid="nav-user-menu">
                  <User className="w-4 h-4" /> {user.name?.split(" ")[0]}
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-navy-surface border-white/10 text-slate-200">
                  <DropdownMenuItem onClick={() => navigate("/portal")} className="cursor-pointer focus:bg-navy-hover" data-testid="nav-portal-link">
                    <User className="w-4 h-4 mr-2" /> Student Portal
                  </DropdownMenuItem>
                  {(user.role === "admin" || user.role === "counsellor") && (
                    <DropdownMenuItem onClick={() => navigate("/admin")} className="cursor-pointer focus:bg-navy-hover" data-testid="nav-admin-link">
                      <ShieldCheck className="w-4 h-4 mr-2" /> Admin Panel
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} className="cursor-pointer focus:bg-navy-hover" data-testid="nav-logout">
                    <LogOut className="w-4 h-4 mr-2" /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/login" className="text-sm font-medium text-slate-300 hover:text-orange-400 transition-colors" data-testid="nav-login">
                Student Login
              </Link>
            )}
            <Button asChild className="bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-full px-5 transition-colors" data-testid="nav-enroll-btn">
              <Link to="/admissions">ENROLL NOW</Link>
            </Button>
          </div>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger className="lg:hidden text-white" data-testid="nav-mobile-menu-btn">
              <Menu className="w-6 h-6" />
            </SheetTrigger>
            <SheetContent side="right" className="bg-navy-base border-white/10 text-slate-200 w-80 overflow-y-auto">
              <div className="flex flex-col gap-1 mt-8">
                <MobileLink to="/" label="Home" setOpen={setOpen} />
                <MobileLink to="/courses" label="All Courses" setOpen={setOpen} />
                {categories.map((c) => (
                  <MobileLink key={c.slug} to={`/courses/category/${c.slug}`} label={c.name} setOpen={setOpen} indent />
                ))}
                {NAV_LINKS.map((l) => (
                  <MobileLink key={l.to} to={l.to} label={l.label} setOpen={setOpen} />
                ))}
                <MobileLink to="/about" label="About XTM" setOpen={setOpen} />
                <MobileLink to="/contact" label="Contact" setOpen={setOpen} />
                <MobileLink to={user ? "/portal" : "/login"} label={user ? "Student Portal" : "Student Login"} setOpen={setOpen} />
                {user && (user.role === "admin" || user.role === "counsellor") && (
                  <MobileLink to="/admin" label="Admin Panel" setOpen={setOpen} />
                )}
                <Button asChild className="bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-full mt-4" data-testid="nav-mobile-enroll-btn">
                  <Link to="/admissions" onClick={() => setOpen(false)}>ENROLL NOW</Link>
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}

function MobileLink({ to, label, setOpen, indent }) {
  return (
    <Link
      to={to}
      onClick={() => setOpen(false)}
      className={`py-2.5 text-sm font-medium text-slate-300 hover:text-orange-400 transition-colors border-b border-white/5 ${indent ? "pl-4 text-slate-400" : ""}`}
      data-testid={`mobile-nav-${label.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
    >
      {label}
    </Link>
  );
}
